// Documents tab: the product's datasheets and specifications (PDF as a rule) — uploaded with a name, renamed,
// reordered (the arrows) and removed here; Save keeps names and order (book {documentSave}), an upload is kept at
// once (book {documentUpload}). A PDF opens in the PDF viewer, as on the product page.
import React, { useEffect, useRef, useState } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { ArrowDownIcon, ArrowUpIcon, CloudArrowUpIcon, DocumentTextIcon, EyeIcon, TrashIcon } from "@heroicons/react/24/outline"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import { UploadDocument, SaveDocuments } from "../../../AdminShared/api"
import { errorMessage } from "../../../AdminShared/ui"
import { Alert, IconButton } from "../shared/shared"
import DocumentViewer from "./DocumentViewer"

// the server's limit (aldoo-com-api product-document-upload)
const MAX_MB = 50
const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
const isPdf = (document) => document?.mime === "application/pdf" || /\.pdf(\?|$)/i.test(document?.path || document?.url || "")

export default function DocumentsTab({ editor }) {
  const t = useProductEditorTexts()
  const { pid, product } = editor
  const [documents, setDocuments] = useState(product?.documents || [])
  const [error, setError] = useState("")
  const [busy, setBusy] = useState(false)
  const [viewing, setViewing] = useState(null)
  const fileInput = useRef(null)
  useEffect(() => setDocuments(product?.documents || []), [product?.documents])

  const upload = async (file) => {
    if (!file) return
    setError("")
    if (file.size > MAX_MB * 1024 * 1024) return setError(t("A document may be at most %s MB", MAX_MB))
    setBusy(true)
    try {
      const res = await UploadDocument(pid, file)
      if (errorMessage(res)) return setError(`${t`Upload failed`}: ${errorMessage(res)}`)
      setDocuments(res.documents || [])
    } catch (e) {
      setError(`${t`Upload failed`}: ${e?.response?.data?.error || e?.message || ""}`)
    } finally {
      setBusy(false)
      if (fileInput.current) fileInput.current.value = ""
    }
  }

  // names and order as they are now; one left out goes (its file too)
  const save = async (next) => {
    setBusy(true)
    const res = await SaveDocuments(pid, next.map(({ path, name }) => ({ path, name }))).catch((e) => ({ error: e?.message }))
    setBusy(false)
    if (errorMessage(res)) return setError(errorMessage(res))
    setDocuments(res.documents || next)
  }
  const move = (index, by) => {
    const next = [...documents]
    const [item] = next.splice(index, 1)
    next.splice(index + by, 0, item)
    save(next)
  }
  const remove = (index) =>
    showPromptDialog({
      variant: "admin",
      title: t`Remove the document`,
      message: documents[index]?.name || "",
      confirmText: t`Remove`,
      cancelText: t`Cancel`,
      onConfirm: () => save(documents.filter((_, i) => i !== index)),
    })

  return (
    <div className="flex flex-col gap-3" data-testid="documents-tab">
      <Typography component="h4" variant="admin_h4" className="my-0">{t`Documents`}</Typography>
      <p className="text-sm text-admin_text_secondary mt-0 mb-1">{t`Datasheets, specifications and manuals. The product page lists them in its Specifications tab; a PDF opens in the site's viewer.`}</p>
      {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}
      <input ref={fileInput} type="file" accept={ACCEPT} className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="documents-file" />

      {documents.map((document, index) => (
        <div key={document.path} className="flex flex-wrap items-center gap-3 p-3 border border-admin_bordered rounded-md" data-testid="documents-row">
          <DocumentTextIcon className="w-7 h-7 text-admin_primary shrink-0" />
          <input
            className="flex-1 min-w-[160px] h-9 px-2 rounded border border-admin_bordered bg-transparent text-sm"
            value={document.name || ""}
            onChange={(e) => setDocuments(documents.map((d, i) => (i === index ? { ...d, name: e.target.value } : d)))}
            onBlur={() => save(documents)}
            aria-label={t`Name`}
          />
          {document.source === "import" && <span className="text-[11px] px-2 py-0.5 rounded-full bg-admin_neutral_1 text-admin_text_secondary">{t`Imported`}</span>}
          {isPdf(document) && document.url && (
            <IconButton title={t`Preview`} onClick={() => setViewing(document)}>
              <EyeIcon className="w-4 h-4" />
            </IconButton>
          )}
          <IconButton title={t`Move up`} onClick={() => move(index, -1)} disabled={busy || index === 0}>
            <ArrowUpIcon className="w-4 h-4" />
          </IconButton>
          <IconButton title={t`Move down`} onClick={() => move(index, 1)} disabled={busy || index === documents.length - 1}>
            <ArrowDownIcon className="w-4 h-4" />
          </IconButton>
          <IconButton title={t`Remove the document`} className="hover:text-admin_error" onClick={() => remove(index)}>
            <TrashIcon className="w-4 h-4" />
          </IconButton>
        </div>
      ))}

      <button
        type="button"
        onClick={() => fileInput.current?.click()}
        disabled={busy}
        className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-admin_bordered rounded-md text-admin_text_secondary hover:border-admin_primary hover:text-admin_primary transition-colors"
        data-testid="documents-upload"
      >
        <CloudArrowUpIcon className="w-7 h-7" />
        <span className="text-sm font-semibold">{t`Add a document`}</span>
        <span className="text-xs">{t("PDF, office or text file, up to %s MB", MAX_MB)}</span>
      </button>
      {busy && (
        <div className="h-1 bg-admin_primary/30 overflow-hidden rounded">
          <div className="h-full w-1/3 bg-admin_primary animate-pulse" />
        </div>
      )}
      {viewing && <DocumentViewer document={viewing} onClose={() => setViewing(null)} />}
    </div>
  )
}
